import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Check, CalendarClock, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];
const DAYS_SHORT = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

const toState = (shifts = []) => {
  const m = {};
  for (let i = 0; i < 7; i++) m[i] = { active: false, start: "", end: "" };
  shifts.forEach((s) => { m[s.day] = { active: true, start: s.start || "", end: s.end || "" }; });
  return m;
};
const toShifts = (m) =>
  Object.entries(m).filter(([, v]) => v.active).map(([day, v]) => ({ day: Number(day), start: v.start, end: v.end }));

function EmployeeCard({ emp, onSaved, onDeleted }) {
  const [name, setName] = useState(emp.name);
  const [days, setDays] = useState(toState(emp.shifts));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(emp.name); setDays(toState(emp.shifts)); }, [emp]);

  const toggle = (d) => setDays((p) => ({ ...p, [d]: { ...p[d], active: !p[d].active } }));
  const setTime = (d, key, val) => setDays((p) => ({ ...p, [d]: { ...p[d], [key]: val } }));

  const save = async () => {
    if (!name.trim()) { toast.error("Skriv et navn"); return; }
    setSaving(true);
    try {
      await api.put(`/employees/${emp.id}`, { name: name.trim(), shifts: toShifts(days) });
      toast.success("Gemt");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunne ikke gemme");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Slet ${emp.name}?`)) return;
    try { await api.delete(`/employees/${emp.id}`); toast.success("Slettet"); onDeleted(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const activeCount = Object.values(days).filter((v) => v.active).length;

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid={`employee-${emp.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)}
          className="max-w-xs bg-[#f8fafc] border-[#e2e8f0] text-slate-800 font-medium" data-testid={`employee-name-${emp.id}`} />
        <span className="text-xs text-slate-500">{activeCount} faste dage</span>
        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" onClick={save} disabled={saving} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400 gap-1.5" data-testid={`employee-save-${emp.id}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem
          </Button>
          <Button size="icon" variant="ghost" onClick={remove} className="h-9 w-9 text-slate-500 hover:text-red-600" data-testid={`employee-delete-${emp.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {DAYS.map((label, d) => {
          const v = days[d];
          return (
            <div key={d} className={`rounded-xl border px-3 py-2.5 transition-colors ${v.active ? "border-[#38BDF8]/40 bg-[#38BDF8]/5" : "border-[#e2e8f0] bg-[#f8fafc]"}`}>
              <button onClick={() => toggle(d)} data-testid={`employee-${emp.id}-day-${d}`}
                className="flex items-center gap-2 w-full text-left">
                <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-colors ${v.active ? "bg-[#38BDF8] text-[#0b1120]" : "bg-[#ffffff] border border-[#e2e8f0] text-transparent"}`}>
                  <Check className="w-3.5 h-3.5" />
                </span>
                <span className={`text-sm font-medium ${v.active ? "text-slate-800" : "text-slate-500"}`}>{label}</span>
              </button>
              {v.active && (
                <div className="flex items-center gap-1.5 mt-2 pl-7">
                  <Input type="time" value={v.start} onChange={(e) => setTime(d, "start", e.target.value)}
                    className="h-8 bg-[#f8fafc] border-[#e2e8f0] text-slate-700 text-xs px-2" data-testid={`employee-${emp.id}-start-${d}`} />
                  <span className="text-slate-500 text-xs">–</span>
                  <Input type="time" value={v.end} onChange={(e) => setTime(d, "end", e.target.value)}
                    className="h-8 bg-[#f8fafc] border-[#e2e8f0] text-slate-700 text-xs px-2" data-testid={`employee-${emp.id}-end-${d}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Vagtplan() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState("overview");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/employees"); setEmployees(r.data); }
    catch { toast.error("Kunne ikke hente vagtplan"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) { toast.error("Skriv et navn"); return; }
    setAdding(true);
    try {
      await api.post("/employees", { name: newName.trim(), shifts: [] });
      setNewName("");
      toast.success("Medarbejder tilføjet");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunne ikke tilføje");
    } finally { setAdding(false); }
  };

  const fmtRange = (s) => (s.start || s.end) ? `${s.start || "?"}–${s.end || "?"}` : "Hele dagen";
  const byDay = (d) => employees
    .map((e) => ({ e, s: (e.shifts || []).find((x) => x.day === d) }))
    .filter((x) => x.s);

  return (
    <div className="max-w-5xl space-y-5" data-testid="vagtplan">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-light text-slate-900 flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-[#38BDF8]" /> Vagtplan
          </h2>
          <p className="text-sm text-slate-500">Registrér faste vagt-ønsker og se hvem der kan arbejde hvilke dage. {employees.length} medarbejdere.</p>
        </div>
        <div className="flex items-center gap-1 rounded-xl bg-[#ffffff] border border-[#e2e8f0] p-1 self-start">
          {[["overview", "Ugeoversigt"], ["edit", "Rediger"]].map(([id, label]) => (
            <button key={id} onClick={() => setView(id)} data-testid={`vagtplan-view-${id}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === id ? "bg-[#38BDF8] text-[#0b1120]" : "text-slate-500 hover:text-slate-900"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500 py-6" data-testid="vagtplan-loading">Henter vagtplan…</p>}

      {/* Ugeoversigt */}
      {!loading && view === "overview" && (
        employees.length === 0 ? (
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-12 text-center text-slate-500" data-testid="vagtplan-overview-empty">
            Ingen medarbejdere endnu. Klik "Rediger" for at tilføje.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="vagtplan-overview">
            {DAYS.map((label, d) => {
              const list = byDay(d);
              return (
                <div key={d} className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4" data-testid={`overview-day-${d}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-base font-medium text-slate-900">{label}</h3>
                    <span className="text-xs text-slate-500">{list.length}</span>
                  </div>
                  <div className="space-y-2">
                    {list.map(({ e, s }) => (
                      <div key={e.id} className="flex items-center justify-between gap-2 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-2.5 py-1.5" data-testid={`overview-day-${d}-emp-${e.id}`}>
                        <span className="text-sm text-slate-700 truncate">{e.name}</span>
                        <span className="text-xs text-sky-600 tabular-nums shrink-0">{fmtRange(s)}</span>
                      </div>
                    ))}
                    {list.length === 0 && <p className="text-xs text-slate-600">Ingen</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Rediger */}
      {!loading && view === "edit" && (
        <div className="space-y-3">
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 flex flex-col sm:flex-row gap-3">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Medarbejderens navn…" className="flex-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="employee-new-name-input" />
            <Button onClick={add} disabled={adding} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400 gap-2" data-testid="employee-add-btn">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Tilføj medarbejder
            </Button>
          </div>
          {employees.map((emp) => (
            <EmployeeCard key={emp.id} emp={emp} onSaved={load} onDeleted={load} />
          ))}
          {employees.length === 0 && (
            <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-12 text-center text-slate-500" data-testid="vagtplan-empty">
              Ingen medarbejdere endnu. Tilføj den første ovenfor.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
